import { useState, useEffect, useCallback, useRef, memo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation, useOutletContext } from "react-router-dom";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { GalleryBanner } from "../components/gallery-banner";
import { useApi } from "../hooks/use-api";
import { useWedding } from "../contexts/wedding-context";
import { createApi } from "../lib/api";
import { cn } from "../lib/utils";
import { cdnUrl } from "../lib/cdn";
import { queryKeys } from "../lib/query-keys";
import { staleTimes, queryClient } from "../lib/query-client";
import {
  Images,
  Loader2,
  X,
  ChevronLeft,
  ChevronRight,

  Play,
  Heart,
  MessageCircle,
  Send,
  Video,
  Search,
  Download,
  FolderOpen,
} from "lucide-react";

const GALLERY_LIMIT = 20;

interface MediaItem {
  id: string;
  kind: "image" | "video";
  r2_key: string;
  created_at: string;
  approved_at?: string;
  uploader_name: string | null;
  like_count: number;
  comment_count: number;
  liked_by_me: boolean;
}

interface Comment {
  id: string;
  content: string;
  author_name: string;
  guest_id: string;
  created_at: string;
  is_mine: boolean;
}

interface PublicAlbum {
  id: string;
  name: string;
  kind: string;
  cover_r2_key: string | null;
  item_count: number;
}

/* ─── Media Card ─────────────────────────────────────── */
const MediaCard = memo(function MediaCard({
  item,
  index,
  onOpen,
}: {
  item: MediaItem;
  index: number;
  onOpen: (i: number) => void;
}) {
  const showReactions = item.like_count > 0 || item.comment_count > 0;
  return (
    <button
      onClick={() => onOpen(index)}
      aria-label={item.uploader_name ? `${item.uploader_name} — ${item.kind}` : item.kind}
      className="group relative aspect-square overflow-hidden rounded-2xl bg-muted cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {item.kind === "image" ? (
        <img
          src={cdnUrl(item.r2_key, { w: 800 })}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
      ) : (
        <>
          <video
            src={`/cdn/${item.r2_key}`}
            className="h-full w-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
          <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-md text-white p-1 rounded-lg">
            <Video className="h-4 w-4" />
          </div>
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <div className="size-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play className="h-7 w-7 fill-white text-white" />
            </div>
          </div>
        </>
      )}

      {/* Desktop hover overlay for images */}
      {item.kind === "image" && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center justify-center gap-6">
          {item.like_count > 0 && (
            <div className="flex items-center gap-1.5 text-white font-bold">
              <Heart className="h-6 w-6 fill-white" />
              <span>{item.like_count}</span>
            </div>
          )}
          {item.comment_count > 0 && (
            <div className="flex items-center gap-1.5 text-white font-bold">
              <MessageCircle className="h-6 w-6 fill-white" />
              <span>{item.comment_count}</span>
            </div>
          )}
        </div>
      )}

      {/* Reactions — always visible bottom-left (mobile) + non-hover desktop */}
      {showReactions && (
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent px-2.5 pb-2 pt-8 md:hidden">
          <div className="flex items-center gap-2">
            {item.like_count > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-white">
                <Heart className="h-3 w-3 fill-white" />
                {item.like_count}
              </span>
            )}
            {item.comment_count > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-white">
                <MessageCircle className="h-3 w-3 fill-white" />
                {item.comment_count}
              </span>
            )}
          </div>
        </div>
      )}
    </button>
  );
});

/* ─── Lightbox ───────────────────────────────────────── */
function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
  onLikeToggle,
  onSocialRefresh,
  showToast,
  slug,
  api,
}: {
  items: MediaItem[];
  index: number;
  onClose: () => void;
  onNavigate: (i: number) => void;
  onLikeToggle: (mediaId: string, liked: boolean, count: number) => void;
  onSocialRefresh: (mediaId: string, likeCount: number, likedByMe: boolean, commentCount: number) => void;
  showToast: (msg: string, ok?: boolean) => void;
  slug: string;
  api: ReturnType<typeof createApi>;
}) {
  const { t, i18n } = useTranslation();
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [showHeart, setShowHeart] = useState(false);
  const lastTapRef = useRef<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const [commentsCursor, setCommentsCursor] = useState<string | undefined>();
  const commentsLoadingRef = useRef(false);

  const fetchComments = useCallback(async (mediaId: string, reset = false) => {
    if (commentsLoadingRef.current) return;
    commentsLoadingRef.current = true;
    setCommentsLoading(true);
    try {
      const cursor = reset ? undefined : commentsCursor;
      const url = `/api/w/${slug}/media/${mediaId}/comments${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const json = await res.json() as {
        ok: boolean; data: Comment[]; hasMore: boolean;
        like_count: number; liked_by_me: boolean; comment_count: number;
      };
      const rows = json.data ?? [];
      setComments(prev => reset ? rows : [...prev, ...rows]);
      setCommentsHasMore(json.hasMore ?? false);
      setCommentsCursor(rows.length > 0 ? rows[rows.length - 1].created_at : undefined);
      if (reset) onSocialRefresh(mediaId, json.like_count ?? 0, json.liked_by_me ?? false, json.comment_count ?? 0);
    } catch {
      // Passive load — callers fire-and-forget, so an uncaught throw here
      // becomes an unhandled rejection. Empty-state UI covers the failure.
    } finally {
      commentsLoadingRef.current = false;
      setCommentsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, commentsCursor]);

  useEffect(() => {
    if (!item?.id) return;
    setComments([]);
    setCommentsHasMore(false);
    setCommentsCursor(undefined);
    commentsLoadingRef.current = false;
    fetchComments(item.id, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  useEffect(() => { setCommentText(""); }, [item?.id]);

  const handleLike = async () => {
    if (likeLoading) return;
    setLikeLoading(true);
    try {
      const res = await api.post<{ liked: boolean; count: number }>(`/media/${item.id}/like`);
      if (res.data) {
        onLikeToggle(item.id, res.data.liked, res.data.count);
        showToast(t(res.data.liked ? "gallery.liked" : "gallery.unlike"));
      }
    } catch { showToast(t("common.error"), false); } finally { setLikeLoading(false); }
  };

  const handleComment = async () => {
    const text = commentText.trim();
    if (!text || commentLoading) return;
    setCommentLoading(true);
    try {
      const res = await api.post<Comment>(`/media/${item.id}/comments`, { content: text });
      setCommentText("");
      showToast(t("gallery.commentAdded"));
      if (res.data) setComments(prev => [...prev, res.data!]);
    } catch { showToast(t("common.error"), false); } finally { setCommentLoading(false); }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await api.del(`/media/${item.id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      showToast(t("gallery.commentDeleted"));
    } catch { showToast(t("common.error"), false); }
  };

  const handleMediaClick = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      if (!item.liked_by_me) handleLike();
      setShowHeart(true);
      setTimeout(() => setShowHeart(false), 700);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onNavigate(index - 1);
      if (e.key === "ArrowRight" && hasNext) onNavigate(index + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasPrev, hasNext, onClose, onNavigate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;
      const dx = e.changedTouches[0].clientX - touchStart.x;
      const dy = e.changedTouches[0].clientY - touchStart.y;
      if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx) && dy > 0) { onClose(); return; }
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0 && hasNext) onNavigate(index + 1);
        if (dx > 0 && hasPrev) onNavigate(index - 1);
      }
      setTouchStart(null);
    },
    [touchStart, index, hasPrev, hasNext, onClose, onNavigate],
  );

  const formattedDate = new Date(item.created_at).toLocaleString(i18n.language, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const commentsEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new comments arrive
  useEffect(() => {
    if (comments.length > 0) {
      commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [comments.length]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 lg:p-12">
      {/* Close button — fixed top-right */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[10000] size-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label={t("gallery.close")}
        title={t("gallery.close")}
      >
        <X className="h-5 w-5" />
      </button>

      {/* Main container — vertical on mobile, horizontal on lg+ */}
      <div
        className="flex flex-col lg:flex-row w-full h-full max-w-7xl rounded-2xl overflow-hidden shadow-2xl"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* ── Left: Media area (70%) ── */}
        <div className="relative flex flex-1 lg:w-[70%] bg-neutral-950 items-center justify-center overflow-hidden">
          {/* Prev arrow */}
          {hasPrev && (
            <button
              onClick={e => { e.stopPropagation(); onNavigate(index - 1); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex size-11 rounded-full bg-black/40 border border-white/10 items-center justify-center hover:bg-black/70 text-white transition-colors cursor-pointer"
              aria-label={t("gallery.prev")}
              title={t("gallery.prev")}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {/* Next arrow */}
          {hasNext && (
            <button
              onClick={e => { e.stopPropagation(); onNavigate(index + 1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex size-11 rounded-full bg-black/40 border border-white/10 items-center justify-center hover:bg-black/70 text-white transition-colors cursor-pointer"
              aria-label={t("gallery.next")}
              title={t("gallery.next")}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}

          {/* Media — double-tap to like */}
          <div
            role="button"
            tabIndex={0}
            aria-label={t("gallery.doubleTapToLike")}
            className="relative w-full h-full flex items-center justify-center cursor-default"
            style={{ touchAction: "manipulation" }}
            onClick={handleMediaClick}
            onKeyDown={e => e.key === "Enter" && handleMediaClick()}
          >
            {item.kind === "image" ? (
              <img
                key={item.id}
                src={cdnUrl(item.r2_key, { w: 1280 })}
                alt=""
                className="max-h-full max-w-full object-contain select-none"
                draggable={false}
              />
            ) : (
              <video
                key={item.id}
                src={`/cdn/${item.r2_key}`}
                className="max-h-full max-w-full object-contain"
                controls
                autoPlay
                playsInline
              />
            )}

            {/* Double-tap heart burst */}
            <div
              className={cn(
                "pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300",
                showHeart ? "opacity-100" : "opacity-0",
              )}
            >
              <Heart className="h-24 w-24 fill-white text-white drop-shadow-2xl" />
            </div>
          </div>

          {/* Counter badge — bottom center of media area */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-medium text-white/60 bg-black/40 px-3 py-1 rounded-full pointer-events-none">
            {index + 1} / {items.length}
          </div>
        </div>

        {/* ── Right: Social panel (30%) ── */}
        <div className="flex flex-col lg:w-[30%] lg:min-w-[340px] border-t border-border lg:border-t-0 lg:border-l border-border max-h-[45vh] lg:max-h-full bg-card">
          {/* Header: uploader info */}
          <div className="flex-shrink-0 flex items-center gap-3 px-5 py-4 border-b border-border">
            <div className="size-10 rounded-full bg-primary/20 border border-border flex items-center justify-center text-sm font-bold text-foreground shrink-0 select-none">
              {item.uploader_name?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-foreground truncate">
                {item.uploader_name ?? t("gallery.guest")}
              </p>
              <p className="text-xs text-muted-foreground">{formattedDate}</p>
            </div>
          </div>

          {/* Scrollable comments area */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
            {commentsLoading && comments.length === 0 ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
              </div>
            ) : comments.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground/60 py-6">
                {t("gallery.noComments")}
              </p>
            ) : (
              <>
                {commentsHasMore && (
                  <button
                    onClick={() => fetchComments(item.id, false)}
                    disabled={commentsLoading}
                    className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5 py-1 cursor-pointer"
                  >
                    {commentsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                    {t("gallery.loadMore")}
                  </button>
                )}
                {comments.map(comment => (
                  <div key={comment.id} className="flex gap-3 group">
                    <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0 select-none">
                      {comment.author_name?.[0]?.toUpperCase() ?? "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground leading-snug">
                        {comment.author_name}
                      </p>
                      <p className="text-sm text-muted-foreground break-words leading-snug mt-0.5">
                        {comment.content}
                      </p>
                      <p className="text-xs text-muted-foreground/50 mt-1">
                        {new Date(comment.created_at).toLocaleString(i18n.language, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    {comment.is_mine && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="shrink-0 text-muted-foreground/30 hover:text-destructive transition-colors mt-0.5 cursor-pointer"
                        aria-label={t("gallery.deleteComment")}
                        title={t("gallery.deleteComment")}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
            <div ref={commentsEndRef} />
          </div>

          {/* Interaction bar: like + download */}
          <div className="flex-shrink-0 border-t border-border px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleLike}
                disabled={likeLoading}
                className="flex items-center gap-2 group transition-transform active:scale-90 cursor-pointer"
                aria-label={t(item.liked_by_me ? "gallery.unlike" : "gallery.like")}
                title={t(item.liked_by_me ? "gallery.unlike" : "gallery.like")}
              >
                <Heart
                  className={cn(
                    "h-6 w-6 transition-all group-hover:scale-110",
                    item.liked_by_me ? "scale-110 fill-rose-500 text-rose-500" : "text-foreground",
                  )}
                />
                <span className="text-sm font-semibold text-foreground">
                  {item.like_count > 0 ? item.like_count : ""}
                </span>
              </button>
              <button
                onClick={() => inputRef.current?.focus()}
                className="flex items-center gap-2 cursor-pointer"
                aria-label={t("gallery.commentPlaceholder")}
                title={t("gallery.commentPlaceholder")}
              >
                <MessageCircle className="h-6 w-6 text-muted-foreground hover:text-foreground transition-colors" />
                <span className="text-sm font-semibold text-muted-foreground">
                  {item.comment_count > 0 ? item.comment_count : ""}
                </span>
              </button>
            </div>
            <a
              href={`/cdn/${item.r2_key}`}
              download
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              aria-label={t("gallery.download")}
            >
              <Download className="h-5 w-5" />
              <span className="text-sm font-medium">{t("gallery.download")}</span>
            </a>
          </div>

          {/* Comment input */}
          <div className="flex-shrink-0 border-t border-border px-4 py-3">
            <label htmlFor="lightbox-comment" className="sr-only">
              {t("gallery.commentPlaceholder")}
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                id="lightbox-comment"
                type="text"
                value={commentText}
                onChange={e => setCommentText(e.target.value.slice(0, 200))}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleComment()}
                placeholder={t("gallery.commentPlaceholder")}
                className="w-full bg-muted border border-border rounded-full py-2.5 pl-5 pr-12 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
              />
              <button
                onClick={handleComment}
                disabled={!commentText.trim() || commentLoading}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 size-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 disabled:opacity-30 transition-opacity cursor-pointer"
                aria-label={t("gallery.share")}
                title={t("gallery.share")}
              >
                {commentLoading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Send className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ─── Gallery Page ───────────────────────────────────── */
export function GalleryPage() {
  const { t } = useTranslation();
  const api = useApi();
  const { slug } = useWedding();
  const location = useLocation();
  const { galleryUnread, markGallerySeen, markActivitySeen } = useOutletContext<{
    galleryUnread: number;
    markGallerySeen: () => void;
    markActivitySeen: () => void;
  }>();
  const incomingOpenId = (location.state as { openMediaId?: string } | null)?.openMediaId ?? null;
  const pendingOpenId = useRef<string | null>(incomingOpenId);
  // Tracks the last incomingOpenId we armed — prevents re-arming on items refetch
  const lastArmedOpenId = useRef<string | null>(incomingOpenId);


  // Gallery state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Filter/sort/search state
  const [activeTab, setActiveTab] = useState<"gallery" | "albums">("gallery");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [filterType, setFilterType] = useState<"all" | "image" | "video">("all");

  // Albums state
  const [activeAlbum, setActiveAlbum] = useState<PublicAlbum | null>(null);
  const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null);

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  }, []);

  // Mark gallery + activity as seen on mount — clears both the gallery badge and the home banner
  useEffect(() => {
    markGallerySeen();
    markActivitySeen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBannerRefresh = useCallback(() => {
    // refetchQueries forces an immediate refetch (invalidateQueries only marks stale)
    void queryClient.refetchQueries({ queryKey: queryKeys.media.all(slug) });
    markGallerySeen();
    // Scroll to top so the newest approved photo (position 0) is visible
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, markGallerySeen]);

  // ── Infinite media query ────────────────────────────────────────────────────
  const {
    data: mediaPages,
    isLoading,
    isFetchingNextPage: loadingMore,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: queryKeys.media.recent(slug, filterType),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const params = new URLSearchParams({ limit: String(GALLERY_LIMIT) });
      if (filterType !== "all") params.set("kind", filterType);
      if (pageParam) params.set("cursor", pageParam);
      const res = await api.get<MediaItem[]>(`/media/recent?${params}`);
      return { data: res.data ?? [], hasMore: (res.data ?? []).length >= GALLERY_LIMIT };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.data[lastPage.data.length - 1]?.approved_at ?? lastPage.data[lastPage.data.length - 1]?.created_at) : undefined,
    staleTime: staleTimes.media,
    enabled: !!slug,
  });

  const items = mediaPages?.pages.flatMap(p => p.data) ?? [];
  const hasMore = hasNextPage ?? false;

  // ── Infinite album items query ──────────────────────────────────────────────
  const {
    data: albumItemPages,
    isLoading: albumItemsLoading,
    isFetchingNextPage: albumLoadingMore,
    fetchNextPage: fetchMoreAlbumItems,
    hasNextPage: albumItemsHasMore,
  } = useInfiniteQuery({
    queryKey: queryKeys.albums.items(slug, selectedAlbum ?? ""),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const qs = pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : "";
      const res = await fetch(`/api/w/${slug}/albums/${selectedAlbum}/media${qs}`, { credentials: "include" });
      const json = await res.json() as { ok: boolean; data: MediaItem[]; hasMore: boolean };
      return { data: (json.data ?? []) as MediaItem[], hasMore: json.hasMore ?? false };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.data[lastPage.data.length - 1]?.created_at : undefined,
    staleTime: staleTimes.albums,
    enabled: !!selectedAlbum,
  });
  const albumItems = albumItemPages?.pages.flatMap(p => p.data) ?? [];

  // ── pendingOpenId: open lightbox once items load ────────────────────────────
  useEffect(() => {
    // Re-arm only when a genuinely new openMediaId arrives from navigation.
    // Skipping duplicates prevents items refetch from re-opening the lightbox
    // after the user has navigated away or closed it.
    if (incomingOpenId && incomingOpenId !== lastArmedOpenId.current) {
      pendingOpenId.current = incomingOpenId;
      lastArmedOpenId.current = incomingOpenId;
    }
    // Don't open while an album is active — lightboxItems would be albumItems
    // (different array from items), causing wrong index to be used
    if (!pendingOpenId.current || items.length === 0 || activeAlbum) return;
    const idx = items.findIndex(r => r.id === pendingOpenId.current);
    if (idx !== -1) {
      lightboxOpenIdRef.current = pendingOpenId.current;
      setLightboxIndex(idx);
      pendingOpenId.current = null; // clear so items refetch doesn't re-trigger
    }
  }, [incomingOpenId, items, activeAlbum]);

  const updateLike = useCallback((mediaId: string, liked: boolean, count: number) => {
    queryClient.setQueryData(
      queryKeys.media.recent(slug, filterType),
      (old: typeof mediaPages) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            data: page.data.map(item =>
              item.id === mediaId ? { ...item, liked_by_me: liked, like_count: count } : item
            ),
          })),
        };
      }
    );
    if (selectedAlbum) {
      queryClient.setQueryData(
        queryKeys.albums.items(slug, selectedAlbum),
        (old: typeof albumItemPages) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: page.data.map(item =>
                item.id === mediaId ? { ...item, liked_by_me: liked, like_count: count } : item
              ),
            })),
          };
        }
      );
    }
    queryClient.invalidateQueries({ queryKey: queryKeys.media.topLiked(slug) });
  }, [slug, filterType, selectedAlbum]);

  const refreshSocial = useCallback(
    (mediaId: string, likeCount: number, likedByMe: boolean, commentCount: number) => {
      queryClient.setQueryData(
        queryKeys.media.recent(slug, filterType),
        (old: typeof mediaPages) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: page.data.map(item =>
                item.id === mediaId
                  ? { ...item, like_count: likeCount, liked_by_me: likedByMe, comment_count: commentCount }
                  : item
              ),
            })),
          };
        }
      );
      if (selectedAlbum) {
        queryClient.setQueryData(
          queryKeys.albums.items(slug, selectedAlbum),
          (old: typeof albumItemPages) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                data: page.data.map(item =>
                  item.id === mediaId
                    ? { ...item, like_count: likeCount, liked_by_me: likedByMe, comment_count: commentCount }
                    : item
                ),
              })),
            };
          }
        );
      }
    },
    [slug, filterType, selectedAlbum],
  );

  const { data: albumsData, isLoading: albumsLoading } = useQuery({
    queryKey: queryKeys.albums.list(slug),
    queryFn: async () => {
      const res = await fetch(`/api/w/${slug}/albums`, { credentials: "include" });
      const json = await res.json() as { ok: boolean; data: PublicAlbum[] };
      return json.data ?? [];
    },
    staleTime: staleTimes.albums,
    enabled: activeTab === "albums",
  });
  const albums = albumsData ?? [];

  // Filtered gallery items — type/sort are server-side, only search is client-side
  const filteredItems = (() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item =>
      item.uploader_name?.toLowerCase().includes(q),
    );
  })();

  // Which items the lightbox operates on (gallery vs album view)
  const lightboxItems = activeAlbum ? albumItems : filteredItems;

  // Track currently open item by ID so the index stays correct when items
  // are refetched/reordered (e.g. re-approval moves a photo to position 0).
  const lightboxOpenIdRef = useRef<string | null>(null);

  const openLightbox = useCallback((i: number) => {
    const source = activeAlbum ? albumItems : filteredItems;
    lightboxOpenIdRef.current = source[i]?.id ?? null;
    setLightboxIndex(i);
  }, [activeAlbum, albumItems, filteredItems]);

  const closeLightbox = useCallback(() => {
    lightboxOpenIdRef.current = null;
    setLightboxIndex(null);
  }, []);

  const navigateLightbox = useCallback((i: number) => {
    lightboxOpenIdRef.current = lightboxItems[i]?.id ?? null;
    setLightboxIndex(i);
  }, [lightboxItems]);

  // When the items list changes while the lightbox is open, keep it pinned to
  // the same media ID instead of the same array index.
  useEffect(() => {
    if (!lightboxOpenIdRef.current) return;
    const newIdx = lightboxItems.findIndex(r => r.id === lightboxOpenIdRef.current);
    if (newIdx !== -1) setLightboxIndex(newIdx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxItems]);

  return (
    <div>
      <GalleryBanner count={galleryUnread} onRefresh={handleBannerRefresh} />

      {/* ── Header ── */}
      <header className="flex flex-col gap-5 mb-6">
        {/* Tab nav */}
        <div className="flex items-end justify-between border-b border-primary/10">
          <div className="flex gap-6 md:gap-8">
            <button
              onClick={() => setActiveTab("gallery")}
              className={cn(
                "pb-4 px-1 border-b-2 font-bold text-base md:text-lg flex items-center gap-2 transition-colors cursor-pointer",
                activeTab === "gallery"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:text-foreground",
              )}
            >
              {t("gallery.title")}
              {items.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {items.length}{hasMore ? "+" : ""}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab("albums"); setActiveAlbum(null); setSelectedAlbum(null); }}
              className={cn(
                "pb-4 px-1 border-b-2 font-medium text-base md:text-lg transition-colors cursor-pointer",
                activeTab === "albums"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:text-foreground",
              )}
            >
              {t("gallery.albums")}
            </button>
          </div>
        </div>

        {/* Search + filters — gallery tab only */}
        {activeTab === "gallery" && (
          <div className="flex items-center gap-2">
            {/* Search — always visible, flex-1 on mobile to fill remaining space */}
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={searchDraft}
                onChange={e => {
                  setSearchDraft(e.target.value);
                  if (e.target.value === "") setSearchQuery("");
                }}
                onKeyDown={e => {
                  if (e.key === "Enter") { setSearchQuery(searchDraft); }
                  if (e.key === "Escape") { setSearchDraft(""); setSearchQuery(""); }
                }}
                placeholder={t("gallery.searchByName")}
                className="w-full appearance-none bg-card border border-primary/10 rounded-xl py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary outline-none transition-[border-color,box-shadow] [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>

            {/* Type filter pills */}
            <div className="flex gap-0.5 bg-muted bg-muted rounded-xl p-1 shrink-0 ml-auto">
              {(["all", "image", "video"] as const).map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={cn(
                    "px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap",
                    filterType === type
                      ? "bg-card shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:text-foreground",
                  )}
                >
                  {type === "all" ? t("gallery.filterAll") : type === "image" ? t("gallery.filterPhotos") : t("gallery.filterVideos")}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* ── Gallery Tab ── */}
      {activeTab === "gallery" && (
        isLoading ? (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-muted bg-muted animate-pulse" />
            ))}
          </section>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Images className="h-8 w-8 text-primary" />
            </div>
            <p className="font-semibold text-foreground">
              {searchQuery || filterType !== "all" ? t("gallery.noResults") : t("gallery.empty")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQuery || filterType !== "all" ? t("gallery.noResultsHint") : t("gallery.emptyHint")}
            </p>
          </div>
        ) : (
          <>
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {filteredItems.map((item, i) => (
                <MediaCard key={item.id} item={item} index={i} onOpen={openLightbox} />
              ))}
            </section>
            {hasMore && !searchQuery && (
              <div className="flex justify-center pt-8">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 rounded-xl border border-primary text-primary px-6 py-2.5 font-semibold hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {loadingMore ? t("common.loading") : t("gallery.loadMore")}
                </button>
              </div>
            )}
          </>
        )
      )}

      {/* ── Albums Tab ── */}
      {activeTab === "albums" && (
        activeAlbum ? (
          /* Album detail view */
          <div>
            <button
              onClick={() => { setActiveAlbum(null); setSelectedAlbum(null); }}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-4 cursor-pointer"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("gallery.backToAlbums")}
            </button>
            <h2 className="font-serif text-xl font-bold text-foreground mb-4">
              {activeAlbum.name}
            </h2>
            {albumItemsLoading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : albumItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <FolderOpen className="h-8 w-8 text-primary" />
                </div>
                <p className="font-semibold text-foreground">{t("gallery.noMedia")}</p>
              </div>
            ) : (
              <>
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                  {albumItems.map((item, i) => (
                    <MediaCard key={item.id} item={item} index={i} onOpen={openLightbox} />
                  ))}
                </section>
                {albumItemsHasMore && (
                  <div className="flex justify-center pt-8">
                    <button
                      onClick={() => fetchMoreAlbumItems()}
                      disabled={albumLoadingMore}
                      className="inline-flex items-center gap-2 rounded-xl border border-primary text-primary px-6 py-2.5 font-semibold hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                      {albumLoadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {albumLoadingMore ? t("common.loading") : t("gallery.loadMore")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* Album list view */
          albumsLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : albums.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <FolderOpen className="h-8 w-8 text-primary" />
              </div>
              <p className="font-semibold text-foreground">{t("gallery.noPublicAlbums")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("gallery.noPublicAlbumsHint")}</p>
            </div>
          ) : (
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {albums.map(album => (
                <button
                  key={album.id}
                  onClick={() => {
                    setActiveAlbum(album);
                    setSelectedAlbum(album.id);
                  }}
                  className="group relative rounded-2xl overflow-hidden bg-muted cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-left"
                >
                  <div className="aspect-square">
                    {album.cover_r2_key ? (
                      <img
                        src={cdnUrl(album.cover_r2_key, { w: 480 })}
                        alt={album.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/5">
                        <FolderOpen className="h-10 w-10 text-primary/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                  <div className="absolute bottom-0 inset-x-0 px-3 pb-3">
                    <p className="font-semibold text-white text-sm leading-tight truncate">{album.name}</p>
                    <p className="text-white/60 text-xs mt-0.5">{album.item_count} {t("gallery.subtitle")}</p>
                  </div>
                </button>
              ))}
            </section>
          )
        )
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && lightboxItems.length > 0 && (
        <Lightbox
          items={lightboxItems}
          index={lightboxIndex}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
          onLikeToggle={updateLike}
          onSocialRefresh={refreshSocial}
          showToast={showToast}
          slug={slug}
          api={api}
        />
      )}

      {/* Toast */}
      {toast !== null && (
        <div
          className={cn(
            "fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-lg pointer-events-none",
            toast.ok ? "bg-green-600" : "bg-red-600",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
